import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, switchMap, withLatestFrom } from 'rxjs';

import { selectAppMode } from 'app/features/settings/state/settings/settings.selectors';
import { StorageService } from 'app/shared/services';
import { resultRegistrationFlowRegistrationDataStorageKey } from 'app/shared/services/storageService';
import { addContainerMeasurementRegistration } from 'app/state/container-measurements/container-measurements.actions';
import { updateGreenHouseMapFilters } from 'app/state/green-house-map/green-house-map.actions';
import { defaultGreenHouseMapFilters } from 'app/state/green-house-map/greenhouse-map.state';
import { ComponentState } from 'app/state/manual-flow/select-state';
import { selectLastMultiUserRecentActivityNumberIfLoaded } from 'app/state/multi-user-preferences/multi-user-preferences.selectors';
import { addPackingRegistration } from 'app/state/registrations/registrations.actions';
import { resetRowSelection } from 'app/state/row-selection/row-selection.actions';
import { selectUserRecentActivityNumberIfLoaded } from 'app/state/user-session/user-session.selectors';

import {
    createContainerMeasurementInResultRegistrationFlow,
    createRegistrationInResultRegistrationFlow,
    goBackInResultRegistrationFlow,
    resetResultRegistrationFlowRegistrationData,
    resultRegistrationFlowComponentStateUpdated,
    setActivityInResultRegistrationFlow,
    setAmountAndProductBoxInResultRegistrationFlow,
    setAmountResultInResultRegistrationFlow,
    setBatchInResultRegistrationFlow,
    setContainerInResultRegistrationFlow,
    setEmployeeInResultRegistrationFlow,
    setLocationInResultRegistrationFlow,
    setResultRegistrationFlowComponentState,
    setRowInResultRegistrationFlow,
    updateResultRegistrationFlowRegistrationDataInStorage,
} from './result-registration-flow.actions';
import {
    selectResultRegistrationComponentState,
    selectResultRegistrationFlowActivity,
    selectResultRegistrationFlowRegistrationData,
} from './result-registration-flow.selectors';
import {
    getMeasurementTypeState,
    skipBatchSelection,
    skipEmployeeSelection,
    skipLocationSelection,
} from './result-registration-flow.utils';

@Injectable()
export class ResultRegistrationEffects {
    updateResultRegistrationFlowRegistrationDataInStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateResultRegistrationFlowRegistrationDataInStorage),
                map((registrationData) => {
                    this.storageService.store(
                        resultRegistrationFlowRegistrationDataStorageKey,
                        registrationData,
                    );

                    return true;
                }),
            ),
        { dispatch: false },
    );

    monitorNextState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                setActivityInResultRegistrationFlow,
                setBatchInResultRegistrationFlow,
                setLocationInResultRegistrationFlow,
                setContainerInResultRegistrationFlow,
                setAmountResultInResultRegistrationFlow,
                setAmountAndProductBoxInResultRegistrationFlow,
                setEmployeeInResultRegistrationFlow,
                setRowInResultRegistrationFlow,
            ),
            withLatestFrom(
                this.store.select(selectResultRegistrationFlowActivity),
                this.store.select(selectResultRegistrationComponentState),
                this.store.select(selectAppMode),
                this.store.select(selectLastMultiUserRecentActivityNumberIfLoaded),
                this.store.select(selectUserRecentActivityNumberIfLoaded),
            ),
            map(
                ([
                    ,
                    activity,
                    currentComponentState,
                    appMode,
                    recentMultiUserActivityNumber,
                    recentUserActivityNumber,
                ]) => ({
                    activity,
                    currentComponentState,
                    appMode,
                    recentActivityNumber:
                        appMode === 'personal' ? recentUserActivityNumber : recentMultiUserActivityNumber,
                }),
            ),
            switchMap(({ activity, currentComponentState, appMode, recentActivityNumber }) => {
                if (!activity) return [];

                if (activity.number !== recentActivityNumber) {
                    this.store.dispatch(
                        updateGreenHouseMapFilters({ greenHouseMapFilters: defaultGreenHouseMapFilters }),
                    );
                }

                switch (currentComponentState) {
                    case ComponentState.SelectActivity:
                        return [
                            setResultRegistrationFlowComponentState({
                                componentState: skipBatchSelection(activity, appMode),
                            }),
                        ];
                    case ComponentState.SelectBatch:
                        return [
                            setResultRegistrationFlowComponentState({
                                componentState: skipLocationSelection(activity, appMode),
                            }),
                        ];
                    case ComponentState.SelectLocation:
                    case ComponentState.SelectRow:
                        return [
                            setResultRegistrationFlowComponentState({
                                componentState: skipEmployeeSelection(appMode),
                            }),
                        ];
                    case ComponentState.SelectContainer:
                        return [
                            setResultRegistrationFlowComponentState({
                                componentState: getMeasurementTypeState(activity.measurementType),
                            }),
                        ];
                    case ComponentState.SelectAmountResult:
                    case ComponentState.SelectAmountAndProductBox:
                        return [
                            setResultRegistrationFlowComponentState({
                                componentState: ComponentState.CreateContainerMeasurement,
                            }),
                        ];
                    case ComponentState.SelectEmployee:
                        return [
                            setResultRegistrationFlowComponentState({
                                componentState: ComponentState.SelectContainer,
                            }),
                        ];
                    default:
                        return [
                            setResultRegistrationFlowComponentState({
                                componentState: skipEmployeeSelection(appMode),
                            }),
                        ];
                }
            }),
        ),
    );

    monitorPreviousState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(goBackInResultRegistrationFlow),
            withLatestFrom(
                this.store.select(selectResultRegistrationFlowActivity),
                this.store.select(selectResultRegistrationComponentState),
                this.store.select(selectAppMode),
            ),
            switchMap(([, activity, currentComponentState, appMode]) => {
                let previousComponentState = currentComponentState;

                if (!activity) return [];
                switch (currentComponentState) {
                    case ComponentState.SelectBatch:
                    case ComponentState.SelectLocation:
                    case ComponentState.SelectRow:
                    case ComponentState.CreateRegistration:
                        previousComponentState = ComponentState.SelectActivity;
                        break;
                    case ComponentState.SelectEmployee:
                        previousComponentState = skipBatchSelection(activity, appMode);
                        break;
                    case ComponentState.SelectAmountResult:
                        previousComponentState = ComponentState.SelectContainer;
                        break;
                    case ComponentState.SelectAmountAndProductBox:
                        previousComponentState = ComponentState.SelectContainer;
                        break;
                    case ComponentState.CreateContainerMeasurement:
                        previousComponentState = ComponentState.SelectContainer;
                        break;
                }

                return [setResultRegistrationFlowComponentState({ componentState: previousComponentState })];
            }),
        ),
    );

    monitorResultRegistrationFlowComponentState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setResultRegistrationFlowComponentState),
            switchMap(({ componentState }) => {
                if (componentState === ComponentState.CreateContainerMeasurement) {
                    return [
                        createContainerMeasurementInResultRegistrationFlow(),
                        goBackInResultRegistrationFlow(),
                        resultRegistrationFlowComponentStateUpdated(),
                    ];
                }

                if (componentState === ComponentState.CreateRegistration) {
                    return [goBackInResultRegistrationFlow()];
                }

                if (componentState === ComponentState.SelectActivity) {
                    return [
                        resetRowSelection(),
                        resultRegistrationFlowComponentStateUpdated(),
                        resetResultRegistrationFlowRegistrationData(),
                    ];
                }

                return [resultRegistrationFlowComponentStateUpdated()];
            }),
        ),
    );

    /* Send registration for container measurement trigger by Next button
     * If Next button is pressed, it should always create only one container measurement registration without labour registration
     */
    createContainerMeasurementInResultRegistrationFlow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createContainerMeasurementInResultRegistrationFlow),
            withLatestFrom(
                this.store.select(selectResultRegistrationFlowActivity),
                this.store.select(selectResultRegistrationFlowRegistrationData),
                this.store.select(selectResultRegistrationComponentState),
            ),
            switchMap(([, activity, registrationData]) => {
                if (!activity) return [];
                const amountResult = registrationData.amountResult;
                const containers = registrationData.containers;
                const employeeNumberMultiUserMode = registrationData.employeeNumber;
                const productBoxRegistrations = registrationData.productBoxRegistrations;
                const location = registrationData.location;

                return [
                    addContainerMeasurementRegistration({
                        activityNumber: activity.number,
                        employeeNumberMultiUserMode,
                        amountResult,
                        containers,
                        productBoxRegistrations,
                        location,
                    }),
                ];
            }),
        ),
    );

    /* Send registration for result registration trigger by Finish button
     * If Finish button is pressed, it should always create only one labour registration with empty container measurement
     */
    createRegistrationInResultRegistrationFlow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createRegistrationInResultRegistrationFlow),
            withLatestFrom(
                this.store.select(selectResultRegistrationFlowActivity),
                this.store.select(selectResultRegistrationFlowRegistrationData),
                this.store.select(selectResultRegistrationComponentState),
            ),
            switchMap(([, activity, registrationData]) => {
                if (!activity) return [];
                const employeeNumberMultiUserMode = registrationData.employeeNumber;
                const location = registrationData.location;
                const batchNumber = registrationData.batchNumber;

                return [
                    addPackingRegistration({
                        activityNumber: activity.number,
                        employeeNumberMultiUserMode,
                        batchNumber,
                        location,
                    }),
                    resetResultRegistrationFlowRegistrationData(),
                    setResultRegistrationFlowComponentState({
                        componentState: ComponentState.CreateRegistration,
                    }),
                ];
            }),
        ),
    );

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly storageService: StorageService,
    ) {}
}
